/* --------------------------------------------------------------------------
    PREMIUM AGENCY FOOTER
    Big Typography | Deep Black Theme | Interaction Heavy
--------------------------------------------------------------------------- */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full bg-[#0F0F0F] text-[#F3F2ED] pt-24 pb-12 border-t border-white/5 font-sans">
      <div className="max-w-[90rem] mx-auto px-6 md:px-12 lg:px-24">
        
        {/* 1. TOP SECTION: Massive Call To Action */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-24 md:mb-36 border-b border-white/10 pb-16">
          <div className="max-w-4xl">
            <h2 className="text-6xl md:text-8xl lg:text-9xl font-semibold tracking-tighter leading-[0.9] mb-10">
              Have an idea?
              <br />
              <span className="text-white/40">Let's build it.</span>
            </h2>
            <a 
              href="mailto:letscreate@framelync.com" 
              className="group inline-flex items-center gap-4 text-xl md:text-3xl font-light border-b border-white/30 pb-2 hover:text-white hover:border-white transition-all"
            >
              letscreate@framelync.com
              <ArrowUpRight className="w-6 h-6 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
            </a>
          </div>

          <div className="mt-12 md:mt-0 text-right hidden md:block self-end">
            <p className="text-xs uppercase tracking-[0.2em] opacity-40 mb-3">Based In</p>
            <p className="text-xl font-medium">Bengaluru / Mysore, IN</p>
          </div>
        </div>

        {/* 2. MIDDLE SECTION: Logo & Links */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-8 mb-24">
          
          {/* Left: HUGE Logo (Col span 6) */}
          <div className="md:col-span-6 flex flex-col justify-between items-start">
            <Link to="/" aria-label="FrameLync Home" className="block -ml-4 md:-ml-6">
              <img
                src="/logo-file-white.png"
                alt="FrameLync"
                // INCREASED SIZE HERE: h-32 -> h-48
                className="h-32 md:h-48 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity duration-500"
                loading="lazy"
              />
            </Link>
            <p className="mt-8 text-lg text-white/40 max-w-sm leading-relaxed pl-2">
              Strategy, Design, & Motion.
              <br/>
              <span className="text-sm opacity-60">Elevating brands through digital excellence.</span>
            </p>
          </div>

          {/* Spacer (Col span 2) */}
          <div className="hidden md:block md:col-span-2"></div>

          {/* Right: Navigation Grid (Col span 4) */}
          <div className="md:col-span-4 flex gap-20 md:gap-24 md:justify-end pt-4">
            
            {/* SITEMAP */}
            <div className="flex flex-col gap-6">
              <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/30 mb-2">Sitemap</h4>
              {['Home', 'Our Works', 'Contact Us'].map((item) => (
                <Link 
                  key={item}
                  to={item === 'Home' ? '/' : `/${item.toLowerCase().replace(' ', '-')}`}
                  className="text-base font-medium hover:text-white hover:translate-x-1 transition-all text-white/60"
                >
                  {item}
                </Link>
              ))}
            </div>

            {/* SOCIALS */}
            <div className="flex flex-col gap-6">
              <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-white/30 mb-2">Connect</h4>
              <SocialLink href="https://instagram.com/framelyncmedia" label="Instagram" />
              <SocialLink href="https://youtube.com/@framelyncmedia" label="YouTube" />
              <SocialLink href="https://wa.me/919035250159" label="WhatsApp" />
              <SocialLink href="mailto:letscreate@framelync.com" label="Email" />
            </div>

          </div>
        </div>

        {/* 3. BOTTOM SECTION: Legal & Copyright */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] md:text-xs uppercase tracking-widest text-white/30">
          <p>© {currentYear} FrameLync Media.</p>
          <p className="hidden md:block">All Rights Reserved.</p>
        </div>

      </div>
    </footer>
  );
};

// Helper Component for Social Links
const SocialLink = ({ href, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex items-center gap-2 text-base font-medium text-white/60 hover:text-white transition-colors"
  >
    {label}
    <ArrowUpRight size={14} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
  </a>
);

export default Footer;